import { NativeSelect, Radio, RadioGroup } from '@odekoteam/doppio';
import { useEffect, useMemo, useState } from 'react';
import { Box, Input, Text } from 'theme-ui';

import {
  BannerDestination,
  buildCampaignDestination,
  destinationChoice,
  destinationFor,
  DestinationChoice,
  parseCampaignDestination,
} from '@/components/Collection/categoryBannerMarkets';
import { CategoryBannerDestinationType } from '@/graphql/types/generated';

export type CollectionOption = { slug: string; name: string };

/** The pickable collections, and whether the list behind them has loaded. */
export type CollectionChoices = {
  options: CollectionOption[];
  status: 'loading' | 'error' | 'ready';
};

type Props = BannerDestination & {
  name: string;
  collections: CollectionChoices;
  disabled?: boolean;
  /** Default off: the campaign-name field is hidden until this is true. */
  customDestinationsEnabled?: boolean;
  onChange: (next: BannerDestination) => void;
};

/**
 * The link choice and collection select only; each caller titles it to match
 * the section it sits in.
 */
export default function CategoryBannerDestination({
  name,
  destinationType,
  destinationRef,
  collections,
  disabled,
  customDestinationsEnabled,
  onChange,
}: Props) {
  const choice = destinationChoice({ destinationType });
  const ready = collections.status === 'ready';
  const parsed = useMemo(
    () => parseCampaignDestination({ destinationType, destinationRef }),
    [destinationType, destinationRef],
  );
  // A stored slug stays selectable while the list is unavailable, and is only
  // called missing once a loaded list proves it is.
  const unlisted =
    parsed.slug && !collections.options.some((c) => c.slug === parsed.slug);
  const items = [
    ...(unlisted
      ? [
          {
            value: parsed.slug,
            label: ready ? `${parsed.slug} (not found)` : parsed.slug,
          },
        ]
      : []),
    ...collections.options.map((c) => ({ value: c.slug, label: c.name })),
  ];
  const placeholder = {
    loading: 'Loading collections...',
    error: 'Collections could not be loaded',
    ready: 'Select a collection...',
  }[collections.status];
  // A campaign already set by an admin whose flag is on is live tracking data
  // this admin cannot see or reason about — edit it blind here (in either
  // direction: stripping it via the Collection select, or reattaching it to a
  // different collection) and it is wrong either way, so it is read-only until
  // their own flag is on, same as a destination this form can't parse at all.
  const hasHiddenCampaign =
    !customDestinationsEnabled && Boolean(parsed.campaignText);
  const notEditableHere = parsed.unsupported || hasHiddenCampaign;
  // The committed campaign text is already slugified (it round-trips through
  // the stored destinationRef), so binding the input straight to it would
  // re-slugify on every keystroke — stripping a trailing "-" right before the
  // next character lands, so a word boundary can never survive real typing.
  // A free-typing draft, committed on blur (same pattern CreateCollectionPromoPage
  // uses for its title→slug field), keeps the field editable like plain text.
  const [campaignDraft, setCampaignDraft] = useState(parsed.campaignText);
  useEffect(() => {
    setCampaignDraft(parsed.campaignText);
  }, [parsed.campaignText]);
  // Live preview of the link this campaign text would build, so the admin can
  // confirm the slugified result before committing it — safe to recompute on
  // every keystroke since it only drives read-only text, never the input's
  // own value (that's what the draft/blur split above is for). Shown whenever
  // a collection is picked, not just once a campaign is set — a plain
  // Collection link has nowhere to carry a query string, so this falls back to
  // the bare catalog path rather than hiding the CTA url entirely.
  const preview = useMemo(
    () => buildCampaignDestination(parsed.slug, campaignDraft),
    [parsed.slug, campaignDraft],
  );
  const previewRef =
    preview.destinationType === CategoryBannerDestinationType.InternalPath
      ? preview.destinationRef
      : `/catalog/collection/${parsed.slug}`;

  return (
    <>
      <RadioGroup
        name={name}
        value={choice}
        onChange={(next: string) =>
          onChange(destinationFor(next as DestinationChoice))
        }
      >
        <Radio value="none" isDisabled={disabled}>
          No link
        </Radio>
        <Radio value="collection" isDisabled={disabled}>
          Collection
        </Radio>
      </RadioGroup>
      {choice === 'collection' &&
        (notEditableHere ? (
          <Box sx={{ mt: 2 }}>
            <Text variant="paragraphSm" sx={{ color: 'neutralDark' }}>
              (custom link, not editable here)
            </Text>
          </Box>
        ) : (
          <>
            <Box sx={{ mt: 2 }}>
              <NativeSelect
                aria-label="Collection"
                value={parsed.slug}
                placeholder={{ label: placeholder, value: '' }}
                items={items}
                isDisabled={disabled || !ready}
                onValueChange={(value) =>
                  onChange(
                    buildCampaignDestination(
                      String(value),
                      parsed.campaignText,
                    ),
                  )
                }
              />
            </Box>
            {customDestinationsEnabled && parsed.slug && (
              <Box sx={{ mt: 2 }}>
                <Text
                  variant="paragraphXs"
                  as="label"
                  sx={{ fontWeight: 'bold' }}
                >
                  Campaign name
                </Text>
                <Input
                  aria-label="Campaign name"
                  value={campaignDraft}
                  disabled={disabled}
                  onChange={(event) => setCampaignDraft(event.target.value)}
                  onBlur={() =>
                    onChange(
                      buildCampaignDestination(parsed.slug, campaignDraft),
                    )
                  }
                />
                <Text
                  variant="paragraphXs"
                  as="label"
                  sx={{ fontWeight: 'bold', display: 'block', mt: 3 }}
                >
                  CTA URL
                </Text>
                <Text
                  variant="paragraphXs"
                  sx={{ color: 'neutralDark', wordBreak: 'break-all' }}
                >
                  {previewRef}
                </Text>
              </Box>
            )}
          </>
        ))}
    </>
  );
}
